// Developer view of the reviewer feedback on one agent: the distilled rules the
// agent reads before every run, and the raw decisions behind them. A library
// agent's feedback is per tenant, so one is picked first.

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  getErrorMessage,
  type AgentDefinition,
  type AgentFeedbackRow,
  type AgentFeedbackRule,
  type Tenant,
} from "../../../api/client.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import { formatDate } from "../../../utils/format.ts";
import { keys } from "../../../api/queryKeys.ts";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { UserCell } from "../../../components/IdentityCell.tsx";
import StatusChip from "../../../components/StatusChip.tsx";

const SOURCE_LABEL: Record<AgentFeedbackRow["source_type"], string> = {
  step_review: "Step",
  brief_review: "Brief",
  prospect_entry: "Company",
  contact_entry: "Contact",
};

function about(row: AgentFeedbackRow): string {
  const c = row.context;
  const name = (c.company ?? c.contact ?? c.headline ?? c.order_title) as string | undefined;
  return name ?? "";
}

function RuleRow({ rule }: { rule: AgentFeedbackRule }) {
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{rule.rule}</Typography>
        {rule.example_quote && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            “{rule.example_quote}”
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={rule.kind === "instruction" ? "instruction" : rule.polarity}
          color={
            rule.kind === "instruction"
              ? "info"
              : rule.polarity === "negative"
                ? "error"
                : rule.polarity === "positive"
                  ? "success"
                  : "default"
          }
        />
      </TableCell>
      <TableCell>{rule.category}</TableCell>
      <TableCell align="right">{rule.support_count}</TableCell>
      <TableCell>{rule.last_seen_at.slice(0, 10)}</TableCell>
    </TableRow>
  );
}

export default function AgentFeedbackDialog({
  agent,
  tenants,
  onClose,
}: {
  agent: AgentDefinition;
  tenants: Tenant[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [tenantId, setTenantId] = useState<string>(agent.tenant_id ?? tenants[0]?.id ?? "");
  const scopeTenant = agent.tenant_id ?? tenantId;
  const { data, isLoading, error } = useQuery({
    queryKey: keys.agentDefinition.feedback(agent.id, scopeTenant || null),
    queryFn: () => api.getAgentFeedback(agent.id, { tenant_id: scopeTenant, limit: 200 }),
    enabled: !!scopeTenant,
  });
  const redistill = useMutation({
    mutationFn: () => api.redistillAgentFeedback(agent.id, scopeTenant),
    onSuccess: () => {
      toast.success("Queued; the distiller rebuilds the digest on its next pass");
      void queryClient.invalidateQueries({
        queryKey: keys.agentDefinition.feedback(agent.id, scopeTenant || null),
      });
    },
  });

  const digest = data?.digest ?? null;
  const other = digest ? Object.entries(digest.other).flatMap(([category, entries]) =>
    entries.map((e) => ({ category, ...e })),
  ) : [];
  const scope = data?.scope ?? null;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Reviewer feedback: {agent.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            What people wrote when approving or rejecting this agent's work, and the rules the
            distiller made of it. Every run of the agent reads the current rules, the decisions
            on its own subject, and can look up the rest.
          </Typography>
          {agent.tenant_id === null && (
            <TextField
              label="Tenant"
              select
              size="small"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              sx={{ maxWidth: 320 }}
              helperText="A library agent learns separately for each tenant."
            >
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isLoading && <CenteredSpinner size={24} />}
          {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
          {data && (
            <>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                  {digest
                    ? `Digest v${digest.version} · ${digest.rules.length} rules from ${digest.decision_count} decisions · built ${formatDate(digest.created_at, "datetime")}`
                    : "No digest yet"}
                </Typography>
                {scope?.building_until && <Chip size="small" label="Building…" color="info" />}
                {scope?.dirty_since && !scope.building_until && (
                  <Tooltip title={`New feedback since ${formatDate(scope.dirty_since, "datetime")}`}>
                    <Chip size="small" label="Rebuild pending" />
                  </Tooltip>
                )}
                <Button
                  size="small"
                  onClick={() => redistill.mutate()}
                  loading={redistill.isPending}
                  disabled={data.total === 0}
                >
                  Re-distill
                </Button>
              </Stack>
              {digest && digest.rules.length > 0 && (
                <ScrollTable>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rule</TableCell>
                      <TableCell>Kind</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Support</TableCell>
                      <TableCell>Last seen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {digest.rules.map((rule) => (
                      <RuleRow key={rule.rule_key} rule={rule} />
                    ))}
                  </TableBody>
                </ScrollTable>
              )}
              {other.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Other reviewer notes (no rule cites them)</Typography>
                  {other.map((entry, i) => (
                    <Typography key={i} variant="body2">
                      · ({entry.category}, {entry.polarity}) {entry.rule}
                    </Typography>
                  ))}
                </Box>
              )}
              {digest && (
                <Box>
                  <Typography variant="subtitle2">As the agent reads it</Typography>
                  <Box
                    component="pre"
                    sx={{
                      whiteSpace: "pre-wrap",
                      fontSize: 12,
                      bgcolor: "action.hover",
                      p: 1,
                      borderRadius: 1,
                      maxHeight: 240,
                      overflow: "auto",
                    }}
                  >
                    {digest.rendered}
                  </Box>
                </Box>
              )}
              <Typography variant="subtitle1">
                Decisions ({data.total})
              </Typography>
              <ScrollTable>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Decision</TableCell>
                    <TableCell>About</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Lesson</TableCell>
                    <TableCell>Who</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.feedback.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <FormattedDate value={row.created_at} />
                      </TableCell>
                      <TableCell>
                        <StatusChip kind="feedback_decision" value={row.decision} />
                        {row.timing === "pre_run" && (
                          <Typography variant="caption" sx={{ display: "block" }}>
                            before the run
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {SOURCE_LABEL[row.source_type]}
                        {about(row) && (
                          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                            {about(row)}
                          </Typography>
                        )}
                        {row.workflow_name && (
                          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                            {row.workflow_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>{row.note ?? "—"}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        {row.lesson?.lessons?.length
                          ? row.lesson.lessons.map((l, i) => (
                              <Typography key={i} variant="caption" sx={{ display: "block" }}>
                                {l.rule}
                              </Typography>
                            ))
                          : row.lesson
                            ? "—"
                            : row.distill_error
                              ? `not yet (${row.distill_error.slice(0, 60)})`
                              : "not yet"}
                      </TableCell>
                      <TableCell>
                        {row.reviewer_name ? (
                          <UserCell displayName={row.reviewer_name} email={null} />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.feedback.length === 0 && (
                    <EmptyTableRow colSpan={6} align="left">
                      No decisions recorded for this agent and tenant yet.
                    </EmptyTableRow>
                  )}
                </TableBody>
              </ScrollTable>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
